"use client";

import {
  motion,
  useMotionValue,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  useEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { ChevronDown, ChevronUp } from "lucide-react";

import { experiences, type ExperienceEntry } from "@/constants";
import { Section } from "@/ui/organism/section";
import { ExperienceIntro } from "./ExperienceIntro";
import { experienceHighlights, focusTracks } from "./content";
import { RadialOrbitalTimeline } from "@/ui/organism/radial-orbital-timeline";
import styles from "@/ui/styles/shared.module.css";
import { StarsBackground } from "@/ui/organism/stars";
import { cn } from "@/utils";

// Calculate radial positions for experiences
const calculateRadialPositions = (count: number, containerSize: number, isMobile: boolean = false) => {
  const positions: Array<{ angle: number; radius: number; x: number; y: number }> = [];
  const centerX = containerSize / 2;
  const centerY = containerSize / 2;

  // Distribute experiences across multiple orbits
  const itemsPerOrbit = 3;
  // Scale base radius and spacing based on mobile/desktop
  const baseRadius = isMobile ? containerSize * 0.15 : Math.min(containerSize * 0.15, 200);
  const orbitSpacing = isMobile ? containerSize * 0.18 : 180;

  Array.from({ length: count }).forEach((_, index) => {
    const orbitIndex = Math.floor(index / itemsPerOrbit);
    const positionInOrbit = index % itemsPerOrbit;
    const radius = baseRadius + orbitIndex * orbitSpacing;

    // Distribute evenly around the circle with offset per orbit
    const itemsInThisOrbit = Math.min(itemsPerOrbit, count - orbitIndex * itemsPerOrbit);
    const angleStep = (2 * Math.PI) / itemsInThisOrbit;
    const orbitOffset = orbitIndex * (Math.PI / 4); // Rotate each orbit slightly
    const angle = (positionInOrbit * angleStep) + orbitOffset;

    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);

    positions.push({ angle: angle * (180 / Math.PI), radius, x, y });
  });

  return positions;
};

// Mobile Experience Card Component
const MobileExperienceCard = ({
  experience,
  index,
  isExpanded,
  onToggle,
}: {
  experience: ExperienceEntry;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="relative"
    >
      {/* Timeline line */}
      {index < experiences.length - 1 && (
        <div className="absolute left-6 top-20 bottom-0 w-0.5 bg-gradient-to-b from-white/20 via-white/10 to-transparent" />
      )}

      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl transition-all duration-300 hover:border-white/20 hover:bg-black/60">
        {/* Glow effect */}
        <div className="pointer-events-none absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-amber-500/0 opacity-0 blur-xl transition-opacity duration-300 hover:opacity-50 hover:from-blue-500/20 hover:via-purple-500/20 hover:to-amber-500/20" />

        {/* Timeline dot */}
        <div className="absolute left-4 top-6 z-10 h-4 w-4 rounded-full border-2 border-white/30 bg-black/60 backdrop-blur-sm" />

        {/* Card content */}
        <button
          onClick={onToggle}
          className="w-full text-left p-4 sm:p-6"
        >
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className="relative h-12 w-12 sm:h-16 sm:w-16 flex-shrink-0 overflow-hidden rounded-xl border border-white/15 bg-black/20">
              <Image
                src={experience.icon}
                alt={experience.company_name}
                fill
                sizes="64px"
                className="object-contain p-2 sm:p-3"
                style={{ backgroundColor: experience.iconBg }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/50 mb-1">
                {experience.date}
              </p>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                {experience.title}
              </h3>
              <p className="text-sm text-white/70 mb-2">{experience.company_name}</p>
              <p className="text-xs text-white/60">{experience.location}</p>
            </div>

            <div className="flex-shrink-0">
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-white/50" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/50" />
              )}
            </div>
          </div>

          {/* Mission preview */}
          <p className="mt-3 text-xs sm:text-sm text-white/70 line-clamp-2 leading-relaxed">
            {experience.mission}
          </p>

          {/* Stack preview */}
          <div className="mt-3 flex flex-wrap gap-2">
            {experience.stack.slice(0, 4).map((item) => (
              <span
                key={`${experience.id}-${item}-preview`}
                className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-white/60"
              >
                {item}
              </span>
            ))}
            {experience.stack.length > 4 && (
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-white/60">
                +{experience.stack.length - 4}
              </span>
            )}
          </div>
        </button>

        {/* Expanded content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-white/10 pt-4">
                {/* Full mission */}
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                    Mission
                  </p>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {experience.mission}
                  </p>
                </div>

                {/* Key points */}
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">
                    Key Achievements
                  </p>
                  <ul className="space-y-2">
                    {experience.points.map((point, idx) => (
                      <motion.li
                        key={`${experience.id}-${idx}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex gap-3 text-sm text-white/80"
                      >
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ffe36c]" />
                        <p className="leading-relaxed">{point}</p>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Full stack */}
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">
                    Tech Stack
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {experience.stack.map((item) => (
                      <span
                        key={`${experience.id}-${item}`}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.15em] text-white/70"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Impact */}
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                    Impact
                  </p>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {experience.impact}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Mobile Experience Timeline
const MobileExperienceTimeline = () => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const toggleCard = (id: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="relative px-4 sm:px-6">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative mx-auto max-w-3xl"
      >
        {/* Background effects */}
        <div className={styles.starfield} aria-hidden="true" />
        <div className={styles.nebula} aria-hidden="true" />

        {/* Timeline container */}
        <div className="relative space-y-6 sm:space-y-8 py-8">
          {experiences.map((experience, index) => (
            <MobileExperienceCard
              key={experience.id}
              experience={experience}
              index={index}
              isExpanded={expandedCards.has(experience.id)}
              onToggle={() => toggleCard(experience.id)}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const Experience = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pinWrapperRef = useRef<HTMLDivElement>(null);
  const timelineSurfaceRef = useRef<HTMLDivElement>(null);
  const scrollProgress = useMotionValue(0);
  const [pinWrapperHeight, setPinWrapperHeight] = useState(3000);
  const [windowWidth, setWindowWidth] = useState(1200);
  const [windowHeight, setWindowHeight] = useState(800);
  const [containerSize, setContainerSize] = useState(1200);
  const [isMobile, setIsMobile] = useState(false);
  const [radialPositions, setRadialPositions] = useState<Array<{ angle: number; radius: number; x: number; y: number }>>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On mobile, use device width with padding; on desktop, use min of width/height
      const size = mobile 
        ? window.innerWidth * 0.85  // 85% of device width on mobile for padding
        : Math.min(window.innerWidth, window.innerHeight) * 0.9;
      setContainerSize(size);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Calculate radial positions only on client to avoid hydration mismatch
  useEffect(() => {
    if (typeof window !== "undefined" && containerSize > 0) {
      setRadialPositions(calculateRadialPositions(experiences.length, containerSize, isMobile));
    }
  }, [containerSize, isMobile]);

  // Calculate scroll progress for reveal
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!pinWrapperRef.current || !containerRef.current) return;
      if (ticking) return;

      ticking = true;
      requestAnimationFrame(() => {
        const pinWrapper = pinWrapperRef.current;
        if (!pinWrapper) {
          ticking = false;
          return;
        }

        const pinRect = pinWrapper.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        const pinWrapperTop = pinRect.top;
        const pinWrapperHeight = pinRect.height;
        const scrollableHeight = pinWrapperHeight - windowHeight;

        if (pinWrapperTop <= 0 && pinWrapperTop >= -scrollableHeight) {
          const progress = Math.abs(pinWrapperTop) / scrollableHeight;
          scrollProgress.set(progress);
        } else if (pinWrapperTop > 0) {
          scrollProgress.set(0);
        } else {
          scrollProgress.set(1);
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrollProgress]);

  const smoothProgress = scrollProgress;

  // Simple rectangle reveal transforms
  const backgroundProgress = useTransform(smoothProgress, [0, 0.3], [0, 1], { clamp: true });
  const cardsRevealProgress = useTransform(smoothProgress, [0.3, 1], [0, 1], { clamp: true });

  const initialSize = 200;
  const surfaceWidth = useTransform(backgroundProgress, [0, 1], [initialSize, windowWidth]);
  const surfaceHeight = useTransform(backgroundProgress, [0, 1], [initialSize, windowHeight]);
  const surfaceTop = useTransform(backgroundProgress, [0, 0.3, 1], [-windowHeight / 3, 0, 0], { clamp: true });
  const surfaceBorderRadius = useTransform(backgroundProgress, [0, 1], ["100%", "0px"]);

  // Calculate height for scroll
  useEffect(() => {
    const calculateHeight = () => {
      const windowHeight = typeof window !== "undefined" ? window.innerHeight : 800;
      const scrollDistance = windowHeight * 2; // Enough space for reveal animation
      const calculatedHeight = windowHeight + scrollDistance;
      setPinWrapperHeight(calculatedHeight);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, []);

  return (
    <Section
      id="work"
      label="Experience"
      order={3}
      fullWidth
      style={{ padding: 0 }}
    >
      <ExperienceIntro highlights={experienceHighlights} tracks={focusTracks} />

      {/* Mobile Experience Timeline - Only visible on mobile */}
      <div className="md:hidden">
        <MobileExperienceTimeline />
      </div>

      {/* Desktop Radial Orbital Timeline - Hidden on mobile */}
      <div className="hidden md:block">
        {/* Pin Wrapper - creates scroll space for reveal */}
        <div
          ref={pinWrapperRef}
          className="relative mt-12"
          style={{ height: `${pinWrapperHeight}px` }}
        >
          {/* Sticky Timeline Container */}
          <div
            ref={containerRef}
            className="sticky top-0 flex items-center justify-center z-10"
            style={{ height: "100vh" }}
          >
            <motion.div
              ref={timelineSurfaceRef}
              className="relative w-full h-full overflow-hidden"
              style={{
                width: surfaceWidth,
                height: surfaceHeight,
                maxWidth: "100vw",
                maxHeight: "100vh",
                borderRadius: surfaceBorderRadius,
                top: surfaceTop,
              }}
            >
              <StarsBackground className="flex w-full h-screen justify-center items-center">
                <div className="flex w-full h-screen justify-center items-start ">
                  <div className="font-sans font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#444444] text-center mt-32 sm:mt-40 md:mt-48 lg:mt-52 px-4">
                    Mission Log
                    <span className="block text-[10px] sm:text-xs text-white/50 text-center mt-2 sm:mt-4">Click to explore</span>
                  </div>
                </div>
              </StarsBackground>

              {/* Radial Orbital Timeline */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                suppressHydrationWarning
              >
                <div
                  className="relative"
                  suppressHydrationWarning
                  style={{
                    width: `${containerSize}px`,
                    height: `${containerSize}px`,
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                >
                {/* Orbital circles */}
                {[0, 1, 2].map((orbitIndex) => {
                  // Scale orbit radii based on container size for better mobile centering
                  const baseRadius = isMobile ? containerSize * 0.15 : 200;
                  const orbitSpacing = isMobile ? containerSize * 0.18 : 180;
                  const radius = baseRadius + orbitIndex * orbitSpacing;
                  return (
                    <motion.div
                      key={orbitIndex}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
                      suppressHydrationWarning
                      style={{
                        width: `${radius * 2}px`,
                        height: `${radius * 2}px`,
                        borderStyle: "dashed",
                        borderWidth: "1px",
                        borderColor: "var(--orbital-border-color)",
                        opacity: "var(--orbital-border-opacity)",
                      }}
                    >
                      {orbitIndex === 0 && (
                        <motion.div
                        className={cn(
                          styles.experienceOrbitShell,
                          "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        )}
                        aria-hidden="true"
                      >
                         <img
                          src="/international-space-station.svg"
                          alt=""
                          className={styles.orbitSatellite}
                          aria-hidden="true"
                        />
                      </motion.div>
                       
                      )}
                    </motion.div>
                  );
                })}

                <RadialOrbitalTimeline
                  timelineData={experiences}
                  revealProgress={cardsRevealProgress}
                />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Experience;
